import { type QueryClient, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { get, post } from 'api/base';
import { trackError } from 'utils/trackError';
import type { ExportStatus, ResultTemplateKey } from 'types/result';
import { ExportStatusValue } from 'types/result';

export interface ExportAsyncPayload {
  resultId: string | number;
  sections: Partial<Record<ResultTemplateKey, true | string[]>>;
}

const EXPORT_RESULT_MUTATION_KEY = ['exportResult'];
const JUST_EXPORTED_WINDOW_MS = 2 * 60 * 1000;
const JUST_EXPORTED_REFETCH_INTERVAL_MS = 2500;
const REFETCH_INTERVAL_MS = 30000;

const exportStatusQueryKey = (id: number | string) => ['exportTaskStatus', Number(id)];

function exportedJustNow(queryClient: QueryClient, id: number | string): boolean {
  const mutation = queryClient.getMutationCache().find({
    mutationKey: EXPORT_RESULT_MUTATION_KEY,
    status: 'success',
    predicate: ({ state }) =>
      Number((state.variables as ExportAsyncPayload | undefined)?.resultId) === Number(id) &&
      Date.now() - state.submittedAt < JUST_EXPORTED_WINDOW_MS,
  });
  return mutation !== undefined;
}

export function useExportResult() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: EXPORT_RESULT_MUTATION_KEY,
    mutationFn: (payload: ExportAsyncPayload) =>
      post('exportResult', { sections: payload?.sections }, [payload.resultId]) as Promise<{
        success: boolean;
      }>,
    onSuccess: (_data, payload) => {
      void queryClient.invalidateQueries({ queryKey: exportStatusQueryKey(payload.resultId) });
    },
    onError: (error) => {
      trackError(error, { context: 'exportResult' });
    },
  });
}

export function useGetExportStatus(
  id: number | string,
  options?: { enabled?: boolean; activeRefetchIntervalMs?: number }
) {
  const queryClient = useQueryClient();
  return useQuery({
    queryKey: exportStatusQueryKey(id),
    queryFn: () => get('exportResult', [id]) as Promise<ExportStatus>,
    enabled: !!id && (options?.enabled ?? true),
    refetchInterval: (query) => {
      const status = query.state.data?.status;
      const isRunning =
        status === ExportStatusValue.Pending ||
        status === ExportStatusValue.Progress ||
        status === ExportStatusValue.Retry;
      if (!isRunning) return false;
      if (options?.activeRefetchIntervalMs) return options.activeRefetchIntervalMs;
      return exportedJustNow(queryClient, id)
        ? JUST_EXPORTED_REFETCH_INTERVAL_MS
        : REFETCH_INTERVAL_MS;
    },
  });
}
